import React, { useEffect, useState, Fragment } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { Box, Paper } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import MuiDatePicker from "../../Common/FormInputs/MuiDatePicker";
import Button from "../../Common/Button/Button";
import { deviceStatusPageSize, startDateLimit } from "./DeviceStatusConstants";
import DisplayDeviceStatusData from "./DisplayDeviceStatusData";
import { format } from "date-fns";
import "./DeviceStatus.css";

const DeviceStatus = ({
    deviceDetails,
    getDeviceStatusData,
    statusData,
    downloadDeviceStatusReport,
    setDialog,
    setDialogContents
}) => {
    const [deviceStatusData, setDeviceStatusData] = useState([]);
    const [startDateFilter, setStartDateFilter] = useState(null);
    const [endDateFilter, setEndDateFilter] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showDeviceStatusDataList, setShowDeviceStatusDataList] = useState(false);
    const [isDataDownloading, setIsDataDownloading] = useState(false)
    const [pageIndex, setPageIndex] = useState(0);

    const getNextPage = () => {
        const newPg = pageIndex + 1
        setPageIndex(newPg);
        fetchDeviceStatusData(newPg)
    }

    const getPrevPage = () => {
        const newPg = pageIndex - 1;
        setPageIndex(newPg);
        fetchDeviceStatusData(newPg)
    }

    const fetchDeviceStatusData = (page = pageIndex, fromTs = formateDate(startDateFilter), toTs = formateDate(endDateFilter)) => {
        setIsLoading(true)
        getDeviceStatusData({
            deviceId: deviceDetails?.deviceId ?? "",
            fromTs,
            toTs,
            index: page,
            size: deviceStatusPageSize,
        }, () => {
            setIsLoading(false);
            setShowDeviceStatusDataList(true)
        })
    };
    const reset = () => {
        setStartDateFilter(new Date())
        setEndDateFilter(new Date())
        setPageIndex(0);
        const todaysDateTime = new Date(new Date()?.setHours(0, 0, 0, 0))
        fetchDeviceStatusData(0, formateDate(todaysDateTime), formateDate(new Date()));
    };
    const filter = () => {
        setPageIndex(0);
        fetchDeviceStatusData(0);
    };
    const handleStartDateChange = (date) => {
        setStartDateFilter(date)
        setEndDateFilter(null);
        setShowDeviceStatusDataList(false)
    }
    const handleEndDateChange = (date) => {
        setEndDateFilter(date);
        setShowDeviceStatusDataList(false)
    }
    function handleDownloadDeviceStatusData() {
        setIsDataDownloading(true)
        downloadDeviceStatusReport({
            fromTs: formateDate(startDateFilter),
            toTs: formateDate(endDateFilter),
            deviceId: deviceDetails.deviceId,
            printableStartDate: `${format(startDateFilter, "dd MMM yyyy")}-${format(endDateFilter, "dd MMM yyyy")}`
        }, () => {
            setIsDataDownloading(false)
        })
    }
    const formateDate = (dateFilter) => {
        if (dateFilter) {
            return dateFilter.getTime()
        }
    }

    useEffect(() => {
        setStartDateFilter(new Date());
        setEndDateFilter(new Date());
        setIsLoading(true);
        const todaysDateTime = new Date(new Date()?.setHours(0, 0, 0, 0))
        getDeviceStatusData({ deviceId: deviceDetails?.deviceId, fromTs: formateDate(todaysDateTime), toTs: Date?.now(), page: 0, size: deviceStatusPageSize },
            () => {
                setIsLoading(false);
                setShowDeviceStatusDataList(true)
            });
        setPageIndex(0);
    }, [deviceDetails?.deviceId, getDeviceStatusData])

    useEffect(() => {
        setDeviceStatusData(statusData)
    }, [statusData])

    return (
        <Fragment>
            <Box sx={{ width: "100%", minWidth: 0 }}>
                <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1 }}>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, minmax(0, 1fr))",
                                lg: "minmax(0, 1fr) minmax(0, 1fr) auto",
                            },
                            gap: 2,
                            alignItems: "center",
                        }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <MuiDatePicker
                                label="Start Date"
                                value={startDateFilter}
                                minDate={(() => {
                                    const d = new Date();
                                    return new Date(d.setDate(d.getDate() - startDateLimit));
                                })()}
                                maxDate={new Date()}
                                onChange={handleStartDateChange}
                            />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <MuiDatePicker
                                label="End Date"
                                value={endDateFilter}
                                minDate={startDateFilter}
                                maxDate={new Date()}
                                onChange={handleEndDateChange}
                            />
                        </Box>
                        <Box>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                                <Button variant="primary" onClick={filter} disabled={startDateFilter === null || endDateFilter === null} sx={{ height: 40 }}>
                                    Filter
                                </Button>
                                <Button variant="ghost" onClick={reset} sx={{ height: 40 }}>
                                    Reset
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleDownloadDeviceStatusData}
                                    disabled={isDataDownloading || startDateFilter === null || endDateFilter === null}
                                    sx={{ height: 40 }}
                                >
                                    {isDataDownloading ? (
                                        <CircularProgress size="1.5rem" sx={{ color: "inherit" }} />
                                    ) : "Download"}
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Paper>
                {isLoading && (
                    <Box sx={{ display: "grid", placeItems: "center", height: "20vh" }}>
                        <CircularProgress size="5em" sx={{ color: "primary.main" }} />
                    </Box>
                )}
                {!isLoading && showDeviceStatusDataList && (
                    <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1, mt: 2, mb: 2 }}>
                        <Box sx={{ overflowX: "auto", overflowY: "auto", maxHeight: 400 }}>
                            <DisplayDeviceStatusData
                                setDialog={setDialog}
                                setDialogContents={setDialogContents}
                                deviceCategory={deviceDetails.category}
                                deviceStatusData={deviceStatusData}
                            />
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mt: 2 }}>
                            <Button variant="primary" onClick={() => getPrevPage()} disabled={pageIndex === 0} sx={{ height: 40 }}>
                                Previous
                            </Button>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Box component="span" sx={{ fontSize: "0.875rem" }}>{`Page : ${pageIndex + 1}`}</Box>
                                <Button
                                    variant="primary"
                                    onClick={() => getNextPage()}
                                    disabled={deviceStatusData?.length < deviceStatusPageSize}
                                    sx={{ height: 40 }}
                                >
                                    Next
                                </Button>
                            </Box>
                        </Box>
                    </Paper>
                )}
            </Box>
        </Fragment>
    );
};

export default DeviceStatus;